import React, { useEffect, useState } from "react";
import { Edit, Trash2, Eye, Download, FileText, RotateCcw } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "./CustomerPaidments.css";

const url = process.env.REACT_APP_BACKEND_URL;

const CustomerPayments = () => {
  const [payments, setPayments] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [filterCollector, setFilterCollector] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // Paid/Pending filter
  const [filterMethod, setFilterMethod] = useState(""); // Payment method filter
  const navigate = useNavigate();

  // Fetch all payments
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const endpoint = showInactive ? `${url}/api/payment/all` : `${url}/api/payment`;
      const res = await axios.get(endpoint);
      setPayments(res.data.data || []);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to fetch payments");
    } finally {
      setLoading(false);
    }
  };

  // Fetch collectors
  const fetchCollectors = async () => {
    try {
      const res = await axios.get(`${url}/api/collectors`);
      setCollectors(res.data.data || []);
    } catch (err) {
      console.error("Error fetching collectors:", err);
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${url}/api/customer`);
      setCustomers(res.data.data || []);
    } catch (err) {
      console.error("Error fetching customers:", err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchPayments(),
      fetchCollectors(),
      fetchCustomers()
    ]);
  }, [showInactive]);

  // ✅ Soft delete payment (deactivate)
  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this payment?")) {
      try {
        const res = await axios.delete(`${url}/api/payment/${id}`);
        alert(res.data.message || "Payment deactivated successfully");
        fetchPayments();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to deactivate payment");
      }
    }
  };

  // ✅ Restore payment (activate)
  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this payment?")) {
      try {
        const res = await axios.put(`${url}/api/payment/${id}/restore`);
        alert(res.data.message || "Payment activated successfully");
        fetchPayments();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to activate payment");
      }
    }
  };

  // ✅ Permanent delete (use with caution)
  const handlePermanentDelete = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this payment? This action cannot be undone.")) {
      try {
        const res = await axios.delete(`${url}/api/payment/${id}/permanent`);
        alert(res.data.message || "Payment permanently deleted");
        fetchPayments();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to delete payment");
      }
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Format date for Excel
  const formatDateForExcel = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Export to Excel
  const exportToExcel = () => {
    const excelData = filteredPayments.map((p, index) => ({
      "S.No": index + 1,
      "Customer Name": p.customer?.customerName || "N/A",
      "House No": p.houseNo || "-",
      "Collector": p.collector ? `${p.collector.fname || ""} ${p.collector.lname || ""}`.trim() : "N/A",
      "Amount": `$${p.amount}`,
      "Payment Method": p.paymentMethod || "-",
      "Payment Status": p.paymentStatus || "Paid",
      "Paid At": formatDateForExcel(p.paidAt || p.createdAt),
      "System Status": p.systemStatus || "active"
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payments");
    
    const date = new Date();
    const fileName = `payments_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export Summary
  const exportSummary = () => {
    const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const paidCount = filteredPayments.filter(p => p.paymentStatus === "Paid").length;
    const pendingCount = filteredPayments.filter(p => p.paymentStatus === "Pending").length;
    const activeCount = filteredPayments.filter(p => p.systemStatus === "active").length;
    const inactiveCount = filteredPayments.filter(p => p.systemStatus === "inactive").length;
    
    const summaryData = [{
      "Report Date": new Date().toLocaleDateString(),
      "Total Payments": filteredPayments.length,
      "Total Amount": `$${totalAmount.toFixed(2)}`,
      "Paid": paidCount,
      "Pending": pendingCount,
      "Active": activeCount,
      "Inactive": inactiveCount,
      "Average Amount": filteredPayments.length > 0 
        ? `$${(totalAmount / filteredPayments.length).toFixed(2)}` 
        : "$0.00"
    }];

    const ws = XLSX.utils.json_to_sheet(summaryData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Summary");
    
    const date = new Date();
    const fileName = `payments_summary_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Apply filters
  const filteredPayments = payments.filter((p) => {
    const matchCollector = filterCollector
      ? p.collector?._id === filterCollector
      : true;

    const matchCustomer = filterCustomer
      ? p.customer?._id === filterCustomer
      : true;

    const matchPaymentStatus = filterStatus
      ? p.paymentStatus === filterStatus
      : true;

    const matchMethod = filterMethod
      ? p.paymentMethod === filterMethod
      : true;

    return matchCollector && matchCustomer && matchPaymentStatus && matchMethod;
  });

  const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidCount = filteredPayments.filter(p => p.paymentStatus === "Paid").length;
  const pendingCount = filteredPayments.filter(p => p.paymentStatus === "Pending").length;
  const activeCount = filteredPayments.filter(p => p.systemStatus === "active").length;
  const inactiveCount = filteredPayments.filter(p => p.systemStatus === "inactive").length;

  if (loading) return <div className="loading">Loading payments...</div>;

  return (
    <div className="payments-container">
      {/* Header with Export Buttons */}
      <div className="header-section">
        <h1 className="page-title">Customer Payments</h1>
        <div className="action-buttons">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button className="summary-btn" onClick={exportSummary}>
            <FileText size={16} />
            Summary Report
          </button>
          <button className="export-btn" onClick={exportToExcel}>
            <Download size={16} />
            Export to Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select
          className="filter-select"
          value={filterCollector}
          onChange={(e) => setFilterCollector(e.target.value)}
        >
          <option value="">All Collectors</option>
          {collectors.map((c) => (
            <option key={c._id} value={c._id}>
              {c.fname} {c.lname}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterCustomer}
          onChange={(e) => setFilterCustomer(e.target.value)}
        >
          <option value="">All Customers</option>
          {customers.map((c) => (
            <option key={c._id} value={c._id}>
              {c.customerName} (House: {c.houseNo})
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Payment Status</option>
          <option value="Paid">Paid</option>
          <option value="Pending">Pending</option>
        </select>

        <select
          className="filter-select"
          value={filterMethod}
          onChange={(e) => setFilterMethod(e.target.value)}
        >
          <option value="">All Methods</option>
          <option value="Cash">Cash</option>
          <option value="Mobile Money">Mobile Money</option>
          <option value="Card">Card</option>
          <option value="EVC">EVC</option>
          <option value="Other">Other</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{filteredPayments.length}</div>
          <div className="stat-label">Total Payments</div>
        </div>
        <div className="stat-card amount">
          <div className="stat-value">${totalAmount.toFixed(2)}</div>
          <div className="stat-label">Total Amount</div>
        </div>
        <div className="stat-card paid">
          <div className="stat-value">{paidCount}</div>
          <div className="stat-label">Paid</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{pendingCount}</div>
          <div className="stat-label">Pending</div>
        </div>
        <div className="stat-card active">
          <div className="stat-value">{activeCount}</div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-value">{inactiveCount}</div>
          <div className="stat-label">Inactive</div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Customer Name</th>
              <th>House No</th>
              <th>Collector</th>
              <th>Amount</th>
              <th>Payment Method</th>
              <th>Payment Status</th>
              <th>Paid At</th>
              <th>System Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No payments found
                </td>
              </tr>
            ) : (
              filteredPayments.map((p) => (
                <tr key={p._id} className={p.systemStatus === 'inactive' ? 'inactive-row' : ''}>
                  <td>{p.customer?.customerName || "N/A"}</td>
                  <td>{p.houseNo || "-"}</td>
                  <td>
                    {p.collector
                      ? `${p.collector.fname || ""} ${p.collector.lname || ""}`.trim()
                      : "N/A"}
                  </td>
                  <td>${p.amount}</td>
                  <td>{p.paymentMethod || "-"}</td>
                  <td>
                    <span className={`badge ${p.paymentStatus === 'Paid' ? 'badge-paid' : 'badge-pending'}`}>
                      {p.paymentStatus || "Paid"}
                    </span>
                  </td>
                  <td>{formatDate(p.paidAt || p.createdAt)}</td>
                  <td>
                    <span className={`badge ${p.systemStatus === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                      {p.systemStatus || "active"}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <button
                        onClick={() => navigate(`/payments/view/${p._id}`)}
                        className="icon-btn view"
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => navigate(`/payments/edit/${p._id}`)}
                        className="icon-btn edit"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      {p.systemStatus === 'active' ? (
                        <button
                          onClick={() => handleSoftDelete(p._id)}
                          className="icon-btn delete"
                          title="Deactivate"
                        >
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestore(p._id)}
                          className="icon-btn restore"
                          title="Activate"
                        >
                          <RotateCcw size={16} /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomerPayments;